import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { MessageCircle, X, Send, Sparkles, Plus, Loader2, ChevronDown } from 'lucide-react';

const AI_API = (process.env.REACT_APP_BACKEND_URL || '') + '/api/booking/ai';

/* ─── Suggestion Card ─── */
function SuggestionCard({ suggestion, lang, onAdd, added }) {
  return (
    <div className="p-3 rounded-sm transition-all"
      style={{ background: added ? 'rgba(201,168,76,0.08)' : 'var(--lux-surface-3)', border: `1px solid ${added ? 'rgba(201,168,76,0.35)' : 'var(--lux-border-2)'}` }}>
      <div className="flex items-start justify-between gap-2 mb-1.5">
        <div className="flex items-center gap-2">
          <span className="text-lg leading-none">{suggestion.emoji}</span>
          <p className="text-sm font-medium" style={{ color: 'var(--lux-text)' }}>{suggestion.title}</p>
        </div>
        {suggestion.price > 0 && (
          <span className="text-xs font-semibold shrink-0" style={{ color: 'var(--lux-gold)' }}>€{suggestion.price}</span>
        )}
      </div>
      <p className="text-xs leading-relaxed mb-3" style={{ color: 'var(--lux-text-muted)' }}>{suggestion.description}</p>
      <button onClick={() => onAdd(suggestion)} disabled={added}
        className="w-full flex items-center justify-center gap-1.5 py-2 text-xs font-medium rounded-sm transition-all"
        style={{ background: added ? 'rgba(5,150,105,0.12)' : 'rgba(201,168,76,0.1)', color: added ? '#059669' : 'var(--lux-gold)', border: `1px solid ${added ? 'rgba(5,150,105,0.3)' : 'rgba(201,168,76,0.25)'}` }}>
        {added ? <><span>✓</span> {lang === 'it' ? 'Aggiunto' : 'Added'}</> : <><Plus size={11} /> {lang === 'it' ? 'Aggiungi al soggiorno' : 'Add to stay'}</>}
      </button>
    </div>
  );
}

/* ─── Main AI Booking Assistant Widget ─── */
export function AIBookingAssistant({ tenantSlug, propertyId, checkin, checkout, guests = 2, lang = 'it', onAddService }) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState('suggestions');
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [added, setAdded] = useState({});
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [autoOpened, setAutoOpened] = useState(false);
  const chatRef = useRef(null);

  // Apri il pannello appena le date cambiano, carica suggerimenti
  useEffect(() => {
    if (checkin && checkout) {
      // Apri subito il pannello
      if (!autoOpened) {
        setTimeout(() => {
          setOpen(true);
          setAutoOpened(true);
        }, 1200); // piccolo delay per non sovrapporre ad altre animazioni
      }
      // Carica suggerimenti (anche senza tenantSlug, prova con fallback)
      loadSuggestions();
    }
  }, [checkin, checkout]);

  useEffect(() => {
    if (chatRef.current) chatRef.current.scrollTop = chatRef.current.scrollHeight;
  }, [messages]);

  const loadSuggestions = async () => {
    setLoading(true);
    try {
      const slug = tenantSlug || propertyId || 'demo';
      const { data } = await axios.post(`${AI_API}/suggest`, {
        tenant_slug: slug,
        property_id: propertyId,
        checkin, checkout, guests, lang,
      });
      if (data.suggestions?.length > 0) {
        setSuggestions(data.suggestions);
      }
    } catch (e) {
      // Log l'errore ma non bloccare il widget
      console.warn('AI suggest:', e.response?.status, e.message);
    } finally {
      setLoading(false);
    }
  };

  const addService = (suggestion) => {
    setAdded(prev => ({ ...prev, [suggestion.id]: true }));
    onAddService?.(suggestion);
  };

  const sendMessage = async () => {
    if (!input.trim() || sending) return;
    const userMsg = { role: 'user', content: input };
    setMessages(prev => [...prev, userMsg]);
    const currentInput = input;
    setInput('');
    setSending(true);
    try {
      const slug = tenantSlug || propertyId || 'demo';
      const { data } = await axios.post(`${AI_API}/chat`, {
        tenant_slug: slug,
        property_id: propertyId,
        checkin: checkin || new Date().toISOString().slice(0, 10),
        checkout: checkout || new Date(Date.now() + 86400000 * 3).toISOString().slice(0, 10),
        guests, lang,
        message: currentInput,
        context: messages.slice(-6),
      });
      setMessages(prev => [...prev, { role: 'assistant', content: data.reply }]);
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: lang === 'it' ? 'Mi dispiace, riprova tra poco.' : 'Sorry, please try again.' }]);
    } finally {
      setSending(false);
    }
  };

  const it = lang === 'it';
  const addedCount = Object.keys(added).length;

  // Mostra il pulsante solo dopo la selezione date
  if (!checkin || !checkout) return null;

  return (
    <>
      {/* Floating trigger button */}
      {!open && (
        <button onClick={() => { setOpen(true); if (suggestions.length === 0) loadSuggestions(); }}
          className="fixed bottom-6 right-4 lg:bottom-8 lg:right-8 z-40 flex items-center gap-2.5 px-4 py-3 rounded-full shadow-2xl"
          style={{ background: '#c9a84c', color: '#000' }}
          data-testid="ai-assistant-btn">
          <Sparkles size={16} />
          <span className="text-sm font-semibold hidden sm:inline">{it ? 'Migliora il soggiorno' : 'Enhance your stay'}</span>
          {addedCount > 0 && (
            <span className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold"
              style={{ background: '#059669', color: '#fff' }}>{addedCount}</span>
          )}
        </button>
      )}

      {/* Panel — bottom sheet on mobile, side panel on desktop */}
      {open && (
        <div className="fixed z-50"
          style={{
            bottom: 0, right: 0,
            width: '100%', maxWidth: '420px',
            height: 'min(90vh, 600px)',
          }}>
          <div className="h-full flex flex-col rounded-t-xl lg:rounded-xl overflow-hidden shadow-2xl"
            style={{ background: '#0d0d0d', border: '1px solid rgba(201,168,76,0.25)' }}>

            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 shrink-0"
              style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-sm flex items-center justify-center" style={{ background: 'rgba(201,168,76,0.12)' }}>
                  <Sparkles size={15} style={{ color: '#c9a84c' }} />
                </div>
                <div>
                  <p className="text-sm font-semibold" style={{ color: '#e5e5e5' }}>{it ? 'Concierge AI' : 'AI Concierge'}</p>
                  <p className="text-[10px]" style={{ color: '#555' }}>{it ? 'Suggerimenti personalizzati' : 'Personalized suggestions'}</p>
                </div>
              </div>
              <button onClick={() => setOpen(false)} style={{ color: '#555' }}><ChevronDown size={20} /></button>
            </div>

            {/* Tabs */}
            <div className="flex shrink-0" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
              {['suggestions', 'chat'].map(t => (
                <button key={t} onClick={() => setTab(t)}
                  className="flex-1 py-2.5 text-xs font-medium"
                  style={{ color: tab === t ? '#c9a84c' : '#555', borderBottom: tab === t ? '2px solid #c9a84c' : '2px solid transparent' }}>
                  {t === 'suggestions'
                    ? (it ? `Idee${suggestions.length > 0 ? ` (${suggestions.length})` : ''}` : `Ideas${suggestions.length > 0 ? ` (${suggestions.length})` : ''}`)
                    : (it ? 'Chiedi al concierge' : 'Ask concierge')}
                </button>
              ))}
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto">
              {tab === 'suggestions' && (
                <div className="p-4 space-y-3">
                  {loading ? (
                    <div className="py-12 text-center">
                      <Loader2 size={24} className="animate-spin mx-auto mb-3" style={{ color: '#c9a84c' }} />
                      <p className="text-xs" style={{ color: '#555' }}>
                        {it ? 'L\'AI sta personalizzando i suggerimenti...' : 'AI is personalizing suggestions...'}
                      </p>
                    </div>
                  ) : suggestions.length > 0 ? (
                    <>
                      <p className="text-[10px] uppercase tracking-widest" style={{ color: '#444' }}>
                        {it ? 'Selezionati per il tuo soggiorno' : 'Selected for your stay'}
                      </p>
                      {suggestions.map((s, i) => (
                        <SuggestionCard key={s.id || i} suggestion={s} lang={lang}
                          onAdd={addService} added={!!added[s.id]} />
                      ))}
                      <button onClick={() => setTab('chat')} className="w-full py-2.5 text-xs rounded-sm"
                        style={{ border: '1px solid rgba(255,255,255,0.08)', color: '#555' }}>
                        {it ? 'Hai domande? Chiedi al concierge →' : 'Questions? Ask the concierge →'}
                      </button>
                    </>
                  ) : (
                    <div className="py-10 text-center">
                      <p className="text-xs mb-3" style={{ color: '#555' }}>
                        {it ? 'Nessun suggerimento disponibile per questa struttura.' : 'No suggestions available for this property.'}
                      </p>
                      <button onClick={() => setTab('chat')} className="text-xs underline" style={{ color: '#c9a84c' }}>
                        {it ? 'Fai una domanda →' : 'Ask a question →'}
                      </button>
                    </div>
                  )}
                </div>
              )}

              {tab === 'chat' && (
                <div className="flex flex-col h-full">
                  <div ref={chatRef} className="flex-1 overflow-y-auto p-4 space-y-3"
                    style={{ maxHeight: 'calc(100% - 60px)' }}>
                    {messages.length === 0 && (
                      <div className="py-8 text-center">
                        <MessageCircle size={24} className="mx-auto mb-3" style={{ color: '#333' }} />
                        <p className="text-xs" style={{ color: '#555' }}>
                          {it
                            ? 'Ciao! Sono il tuo concierge virtuale. Chiedimi qualsiasi cosa sul tuo soggiorno.'
                            : 'Hello! I\'m your virtual concierge. Ask me anything about your stay.'}
                        </p>
                        <div className="flex flex-wrap gap-1.5 justify-center mt-4">
                          {(it
                            ? ['Cosa fare in zona?', 'Ristoranti consigliati?', 'Come raggiungere il centro?']
                            : ['What to do nearby?', 'Restaurant recommendations?', 'How to get to the center?']
                          ).map(q => (
                            <button key={q} onClick={() => setInput(q)}
                              className="text-[10px] px-2.5 py-1.5 rounded-full"
                              style={{ border: '1px solid rgba(201,168,76,0.2)', color: '#c9a84c' }}>
                              {q}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    {messages.map((m, i) => (
                      <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                        <div className="max-w-[85%] px-3.5 py-2.5 rounded-xl text-xs leading-relaxed"
                          style={{
                            background: m.role === 'user' ? '#c9a84c' : 'rgba(255,255,255,0.06)',
                            color: m.role === 'user' ? '#000' : '#ccc',
                            borderRadius: m.role === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                          }}>
                          {m.content}
                        </div>
                      </div>
                    ))}
                    {sending && (
                      <div className="flex justify-start">
                        <div className="px-3.5 py-2.5 rounded-xl" style={{ background: 'rgba(255,255,255,0.06)' }}>
                          <Loader2 size={12} className="animate-spin" style={{ color: '#555' }} />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Chat input (solo nel tab chat) */}
            {tab === 'chat' && (
              <div className="shrink-0 flex items-center gap-2 p-3"
                style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                <input
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendMessage()}
                  placeholder={it ? 'Chiedi qualcosa...' : 'Ask something...'}
                  className="flex-1 px-3 py-2.5 text-xs rounded-sm"
                  style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)', color: '#ccc' }}
                  data-testid="ai-chat-input"
                />
                <button onClick={sendMessage} disabled={sending || !input.trim()}
                  className="w-9 h-9 rounded-sm flex items-center justify-center shrink-0"
                  style={{ background: input.trim() ? '#c9a84c' : 'rgba(255,255,255,0.05)', color: input.trim() ? '#000' : '#444' }}>
                  <Send size={14} />
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
